import { useState, useEffect, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  PlusIcon, ArrowDownTrayIcon,
  FolderIcon, Squares2X2Icon, CheckCircleIcon, ArrowPathIcon,
  ExclamationCircleIcon, EyeIcon, SparklesIcon,
  CodeBracketSquareIcon,
  MapPinIcon, UserIcon, MagnifyingGlassIcon, XMarkIcon,
  CalendarDaysIcon, TableCellsIcon, DocumentDuplicateIcon,
  PauseCircleIcon, NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { BoltIcon, RocketLaunchIcon } from '@heroicons/react/24/solid';
import { apiClient } from '../../lib/axios';
import { downloadBlob } from '../../lib/downloadBlob';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { subscribeToUserChannel, ProgressEvent } from '../../lib/echo';
import { useAuthStore } from '../../store/useAuthStore';

interface SearchTask {
  id: string;
  type: string;
  company: string;
  location?: string;
  search_titles?: string | string[];
  depth?: number;
  status: string;
  progress: number;
  total_records: number;
  processed_records: number;
  leads_found?: number;
  results: string | null;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  credits_used: number;
}

interface Metrics {
  total_companies_uploaded: number;
  companies_enriched: number;
  total_decision_makers_found: number;
}

const DEPTH_LABELS: Record<number, string> = {
  1: 'Normal Search',
  2: 'Deep Search ×1',
  3: 'Deep Search ×3',
  4: 'Deep Search ×4',
  5: 'Deep Search ×5',
};

function parseTitles(d?: string | string[]): string[] {
  if (!d) return [];
  if (Array.isArray(d)) return d;
  try { return JSON.parse(d); } catch { return []; }
}

function SearchParamsDialog({ task, open, onClose }: { task: SearchTask | null; open: boolean; onClose: () => void }) {
  if (!task) return null;
  const titles = parseTitles(task.search_titles);

  return (
    <Dialog.Root open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-white rounded-2xl shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">

          {/* Header */}
          <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                task.type === 'bulk' ? 'bg-slate-100 text-slate-500' : 'bg-brand-50 text-brand-500'
              }`}>
                {task.type === 'bulk' ? <FolderIcon className="w-4 h-4" /> : <Squares2X2Icon className="w-4 h-4" />}
              </div>
              <div>
                <Dialog.Title className="text-base font-extrabold text-slate-900 leading-tight">{task.company}</Dialog.Title>
                <p className="text-[11px] text-slate-400 font-medium mt-0.5 capitalize">{task.type} search</p>
              </div>
            </div>
            <Dialog.Close className="w-8 h-8 flex items-center justify-center rounded-xl text-slate-400 hover:text-brand-600 hover:bg-slate-100 transition-colors">
              <XMarkIcon className="w-4 h-4" />
            </Dialog.Close>
          </div>

          {/* Params */}
          <div className="px-6 py-5 flex flex-col gap-4">

            {/* Company */}
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                <MagnifyingGlassIcon className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Company</p>
                <p className="text-sm font-semibold text-slate-900">{task.company}</p>
              </div>
            </div>

            {/* Location */}
            {task.location && (
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                  <MapPinIcon className="w-3.5 h-3.5 text-slate-500" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Location</p>
                  <p className="text-sm font-semibold text-slate-900">{task.location}</p>
                </div>
              </div>
            )}

            {/* Roles searched */}
            {titles.length > 0 && (
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                  <UserIcon className="w-3.5 h-3.5 text-slate-500" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Roles Searched</p>
                  <div className="flex flex-wrap gap-1.5">
                    {titles.map((t, i) => (
                      <span key={i} className="px-2.5 py-1 bg-brand-50 text-brand-700 border border-brand-100 text-[11px] font-semibold rounded-full">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Depth */}
            {task.depth && (
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                  <SparklesIcon className="w-3.5 h-3.5 text-slate-500" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Search Depth</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{DEPTH_LABELS[task.depth] ?? `×${task.depth}`}</span>
                    <span className="px-2 py-0.5 bg-brand-50 text-slate-800 text-[10px] font-bold rounded-full">{task.depth} credit{task.depth > 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Records (bulk only) */}
            {task.type === 'bulk' && (
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                  <TableCellsIcon className="w-3.5 h-3.5 text-slate-500" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Total Records</p>
                  <p className="text-sm font-semibold text-slate-900">{task.total_records.toLocaleString()} companies</p>
                </div>
              </div>
            )}

            {/* Credits used */}
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg bg-brand-50 flex items-center justify-center shrink-0 mt-0.5">
                <BoltIcon className="w-3.5 h-3.5 text-brand-500" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Credits Used</p>
                <p className="text-sm font-semibold text-brand-600">{(task.credits_used ?? 0).toLocaleString()}</p>
              </div>
            </div>

            {/* Date */}
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                <CalendarDaysIcon className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Created</p>
                <p className="text-sm font-semibold text-slate-900">
                  {new Date(task.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 pb-6 flex items-center gap-3">
            <Link
              to={`/dashboard/search_history/${task.id}`}
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-2 bg-brand-500 text-slate-900 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors [box-shadow:0_4px_12px_rgba(0,84,214,0.3)]"
            >
              <EyeIcon className="w-4 h-4" /> View Full Results
            </Link>
            <Dialog.Close className="px-4 py-2.5 rounded-xl bg-slate-100 text-slate-600 text-sm font-bold hover:bg-slate-200 transition-colors">
              Close
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default function SearchHistory() {
  useDocumentTitle('Search History', 'Review and export all your past AI-powered decision maker searches.');

  const { user } = useAuthStore();
  const [tasks, setTasks] = useState<SearchTask[]>([]);
  const [metrics, setMetrics] = useState<Metrics>({ total_companies_uploaded: 0, companies_enriched: 0, total_decision_makers_found: 0 });
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTask, setSelectedTask] = useState<SearchTask | null>(null);

  const fetchHistory = useCallback(async (page = currentPage) => {
    try {
      const response = await apiClient.get('/search/history', { params: { page, per_page: 10 } });
      setTasks(response.data.tasks);
      setMetrics(response.data.metrics);
      setMeta(response.data.meta);
    } catch {}
    finally { setLoading(false); }
  }, [currentPage]);

  useEffect(() => {
    fetchHistory(currentPage);
    const interval = setInterval(() => fetchHistory(currentPage), 6000);
    return () => clearInterval(interval);
  }, [currentPage]);

  // WebSocket: patch individual tasks in real-time as progress events arrive
  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeToUserChannel(user.id, (e: ProgressEvent) => {
      setTasks(prev => prev.map(t => {
        if (t.id !== e.task_id) return t;
        if (e.status === 'completed' || e.status === 'failed') {
          // Full refresh so completed_at and final counts are accurate
          fetchHistory(currentPage);
          return t;
        }
        // processed_records = companies processed; leads_found = decision makers found
        // The broadcast event sends companies_processed as leads_found during processing
        // and actual leads only at completion — keep them separate
        const pct = t.total_records > 0
          ? Math.min(98, Math.round(((e.leads_found ?? t.processed_records) / t.total_records) * 100))
          : e.progress;
        return {
          ...t,
          status: e.status,
          progress: pct,
          processed_records: e.leads_found ?? t.processed_records,
        };
      }));
    });
    return unsubscribe;
  }, [user?.id]);

  const handleDownload = async (taskId: string, companyName: string) => {
    try {
      const ts = new Date().toISOString().slice(0, 19).replace(/[-T:]/g, (c) => (c === 'T' ? '_' : c));
      await downloadBlob(`/search/export/download/${taskId}`, `${companyName}_${ts}_export.csv`);
    } catch {
      alert('Download failed. The file may still be processing.');
    }
  };

  const formatDate = (s: string) =>
    new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatDuration = (task: SearchTask): string => {
    const start = task.started_at ?? task.created_at;
    const end   = task.completed_at;
    if (!end) return '—';
    const secs = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000);
    if (secs < 0)   return '—';
    if (secs < 60)  return `${secs}s`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const getLeadCount = (task: SearchTask) => {
    // leads_found is the canonical de-duplicated count the backend attaches to
    // every task (bulk and single) — use it so the list matches the detail page.
    if (task.leads_found !== undefined && task.leads_found !== null) {
      return task.leads_found;
    }
    // Fallback for older single-search rows: parse results JSON for the count
    if (task.results) {
      try {
        const parsed = JSON.parse(task.results);
        if (typeof parsed.count === 'number') return parsed.count;
        if (Array.isArray(parsed)) return parsed.length;
      } catch {}
    }
    return task.processed_records || 0;
  };

  const getSubtitle = (task: SearchTask) => {
    const parts: string[] = [];
    if (task.type === 'bulk' && task.total_records) parts.push(`${task.total_records.toLocaleString()} rows`);
    if (task.location) parts.push(task.location);
    if (task.depth) parts.push(DEPTH_LABELS[task.depth] ?? `×${task.depth}`);
    return parts.join(' · ');
  };

  const totalPages = meta.last_page;
  const start = meta.total === 0 ? 0 : (meta.page - 1) * meta.per_page + 1;
  const end = Math.min(meta.page * meta.per_page, meta.total);

  function StatusBadge({ status, progress, processedRecords, totalRecords }: { status: string; progress: number; processedRecords: number; totalRecords: number }) {
    if (status === 'completed') return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-brand-500 text-slate-900 text-[11px] font-bold rounded-full">
        <CheckCircleIcon className="w-3.5 h-3.5" /> Ready
      </span>
    );
    if (status === 'failed') return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-600 text-[11px] font-bold rounded-full">
        <ExclamationCircleIcon className="w-3.5 h-3.5" /> Failed
      </span>
    );
    if (status === 'cancelled') return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-500 text-[11px] font-bold rounded-full">
        <NoSymbolIcon className="w-3.5 h-3.5" /> Cancelled
      </span>
    );
    if (status === 'paused') {
      const pausedLabel = totalRecords > 0 ? `${processedRecords} / ${totalRecords}` : 'Paused';
      const pausedPct = totalRecords > 0 ? Math.min(98, Math.round((processedRecords / totalRecords) * 100)) : Math.max(1, progress || 0);
      return (
        <div className="flex flex-col gap-1.5">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-600 text-[11px] font-bold rounded-full w-fit">
            <PauseCircleIcon className="w-3.5 h-3.5" /> {pausedLabel}
          </span>
          <div className="w-28 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${pausedPct}%` }} />
          </div>
        </div>
      );
    }
    const pct = totalRecords > 0
      ? Math.min(98, Math.round((processedRecords / totalRecords) * 100))
      : Math.max(1, progress || 0);
    const label = totalRecords > 0
      ? `${processedRecords} / ${totalRecords}`
      : `${pct}%`;
    return (
      <div className="flex flex-col gap-1.5">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-500 text-[11px] font-bold rounded-full w-fit">
          <ArrowPathIcon className="w-3 h-3 animate-spin" /> {label}
        </span>
        <div className="w-28 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-brand-400 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50/50">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .fade-in { animation: fadeIn 0.25s ease-out both; }
      `}</style>

      <SearchParamsDialog
        task={selectedTask}
        open={!!selectedTask}
        onClose={() => setSelectedTask(null)}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-6 sm:py-8 flex flex-col gap-6">

          {/* Page heading */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Search History</h1>
              <p className="text-sm text-slate-500 mt-1">Manage your lead datasets and monitor export progress.</p>
            </div>
            <Link
              to="/dashboard/search"
              className="flex items-center gap-2 bg-brand-500 text-slate-900 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors [box-shadow:0_4px_16px_rgba(0,84,214,0.35)]"
            >
              <PlusIcon className="w-3.5 h-3.5" /> New Search
            </Link>
          </div>

          {/* Metric cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: FolderIcon,        iconBg: 'bg-blue-50',    iconColor: 'text-blue-500',    label: 'Companies Uploaded',      value: metrics.total_companies_uploaded },
              { icon: CheckCircleIcon,   iconBg: 'bg-emerald-50', iconColor: 'text-emerald-500', label: 'Companies Enriched',      value: metrics.companies_enriched },
              { icon: UserIcon,          iconBg: 'bg-brand-50',   iconColor: 'text-brand-500',   label: 'Decision Makers Found',   value: metrics.total_decision_makers_found },
            ].map((m, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">{m.label}</p>
                  <p className="text-[32px] font-extrabold text-slate-900 leading-none">
                    {loading ? '—' : m.value.toLocaleString()}
                  </p>
                </div>
                <div className={`w-11 h-11 rounded-xl ${m.iconBg} flex items-center justify-center shrink-0`}>
                  <m.icon className={`w-5 h-5 ${m.iconColor}`} />
                </div>
              </div>
            ))}
          </div>

          {/* Table card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

            {/* Table header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <span className="text-base font-extrabold text-slate-900">All Searches</span>
                <span className="px-2.5 py-0.5 bg-slate-100 text-slate-500 text-[11px] font-bold rounded-full">
                  {meta.total} items
                </span>
              </div>
              {/* <div className="flex items-center gap-2 text-slate-400">
                <AdjustmentsHorizontalIcon className="w-4 h-4" />
                <ListBulletIcon className="w-4 h-4" />
              </div> */}
            </div>

            {/* ── Mobile card list (< sm) ── */}
            <div className="sm:hidden divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="px-4 py-4 flex flex-col gap-3 animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-slate-100 shrink-0" />
                      <div className="flex-1">
                        <div className="h-4 bg-slate-100 rounded w-2/3 mb-1.5" />
                        <div className="h-3 bg-slate-100 rounded w-1/3" />
                      </div>
                      <div className="h-5 w-16 bg-slate-100 rounded-full" />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="h-3 bg-slate-100 rounded w-24" />
                      <div className="h-7 w-14 bg-slate-100 rounded-lg" />
                    </div>
                  </div>
                ))
              ) : tasks.length === 0 ? (
                <div className="px-6 py-16 text-center">
                  <RocketLaunchIcon className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-700 text-sm font-bold mb-1">No searches yet</p>
                  <Link to="/dashboard/search" className="text-sm font-bold text-slate-600 hover:text-slate-900 hover:underline transition-colors">
                    Start your first search →
                  </Link>
                </div>
              ) : (
                tasks.map((task, idx) => (
                  <div
                    key={task.id}
                    className="px-4 py-4 fade-in"
                    style={{ animationDelay: `${idx * 30}ms` }}
                  >
                    {/* Row 1: icon + name + status */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                        task.type === 'bulk' ? 'bg-slate-100 text-slate-500' : 'bg-brand-50 text-brand-500'
                      }`}>
                        {task.type === 'bulk'
                          ? <FolderIcon className="w-4 h-4" />
                          : <Squares2X2Icon className="w-4 h-4" />
                        }
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-slate-900 truncate">{task.company}</p>
                        {getSubtitle(task) && (
                          <p className="text-[11px] text-slate-400 font-medium mt-0.5 truncate">{getSubtitle(task)}</p>
                        )}
                      </div>
                      <StatusBadge status={task.status} progress={task.progress ?? 0} processedRecords={task.processed_records ?? 0} totalRecords={task.total_records ?? 0} />
                    </div>

                    {/* Row 2: meta + actions */}
                    <div className="flex items-center justify-between gap-2 pl-12">
                      <div className="flex items-center gap-3 text-[12px] text-slate-500 min-w-0">
                        <span>{formatDate(task.created_at)}</span>
                        <span className="font-extrabold text-slate-900">
                          {getLeadCount(task).toLocaleString()} <span className="font-normal text-slate-400">found</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          onClick={() => setSelectedTask(task)}
                          className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors"
                          title="View params"
                        >
                          <MagnifyingGlassIcon className="w-3.5 h-3.5" />
                        </button>
                        {task.status === 'completed' && (
                          <button
                            onClick={() => handleDownload(task.id, task.company)}
                            className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors"
                            title="Export CSV"
                          >
                            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <Link
                          to={`/dashboard/search_history/${task.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-50 hover:bg-brand-100 text-slate-800 text-[12px] font-semibold transition-colors"
                        >
                          <EyeIcon className="w-3.5 h-3.5" /> View
                        </Link>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* ── Desktop table (sm+) ── */}
            <table className="hidden sm:table w-full text-left table-fixed">
              <thead>
                <tr className="border-b border-slate-100 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="px-6 py-3 border-r border-slate-100 w-[22%]">List Name</th>
                  <th className="px-6 py-3 border-r border-slate-100 w-[10%]">Job ID</th>
                  <th className="px-6 py-3 border-r border-slate-100 w-[11%]">Date Created</th>
                  <th className="px-6 py-3 border-r border-slate-100 w-[8%]">Duration</th>
                  <th className="px-6 py-3 border-r border-slate-100 w-[9%]">People Found</th>
                  <th className="px-6 py-3 border-r border-slate-100 w-[14%]">Export Status</th>
                  <th className="px-6 py-3 text-right w-[26%]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      {/* List Name */}
                      <td className="px-6 py-4 border-r border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-slate-100 shrink-0" />
                          <div className="flex-1 space-y-1.5 min-w-0">
                            <div className="h-3.5 bg-slate-100 rounded-full w-3/4" />
                            <div className="h-2.5 bg-slate-100 rounded-full w-1/2" />
                          </div>
                        </div>
                      </td>
                      {/* Job ID */}
                      <td className="px-6 py-4 border-r border-slate-100">
                        <div className="h-3 bg-slate-100 rounded-full w-full" />
                      </td>
                      {/* Date Created */}
                      <td className="px-6 py-4 border-r border-slate-100">
                        <div className="h-3 bg-slate-100 rounded-full w-4/5" />
                      </td>
                      {/* Duration */}
                      <td className="px-6 py-4 border-r border-slate-100">
                        <div className="h-3 bg-slate-100 rounded-full w-3/5 mx-auto" />
                      </td>
                      {/* People Found */}
                      <td className="px-6 py-4 border-r border-slate-100">
                        <div className="h-4 bg-slate-100 rounded-full w-1/2 mx-auto" />
                      </td>
                      {/* Export Status */}
                      <td className="px-6 py-4 border-r border-slate-100">
                        <div className="h-6 bg-slate-100 rounded-full w-4/5" />
                      </td>
                      {/* Actions */}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <div className="h-7 bg-slate-100 rounded-lg w-16" />
                          <div className="h-7 bg-slate-100 rounded-lg w-14" />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : tasks.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-20 text-center">
                      <RocketLaunchIcon className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="text-brand-700 text-sm font-bold mb-1">No searches yet</p>
                      <Link to="/dashboard/search" className="text-sm font-bold text-brand-700 hover:underline">
                        Start your first search →
                      </Link>
                    </td>
                  </tr>
                ) : (
                  tasks.map((task, idx) => (
                    <tr
                      key={task.id}
                      className="fade-in group hover:bg-slate-50/60 transition-colors"
                      style={{ animationDelay: `${idx * 30}ms` }}
                    >
                      {/* List name */}
                      <td className="px-6 py-4 border-r border-slate-100 max-w-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                            task.type === 'bulk' ? 'bg-slate-100 text-slate-500' : 'bg-brand-50 text-brand-500'
                          }`}>
                            {task.type === 'bulk'
                              ? <FolderIcon className="w-4 h-4" />
                              : <Squares2X2Icon className="w-4 h-4" />
                            }
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-900 truncate" title={task.company}>{task.company}</p>
                            {getSubtitle(task) && (
                              <p className="text-[11px] text-slate-400 font-medium mt-0.5 truncate">{getSubtitle(task)}</p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Job ID */}
                      <td className="px-6 py-4 border-r border-slate-100 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-[11px] font-mono text-slate-400">
                            #{task.id.substring(0, 8).toUpperCase()}
                          </span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(task.id).then(() => {
                                toast.success('✓ Job ID copied');
                              }).catch(() => {
                                toast.error('Failed to copy');
                              });
                            }}
                            className="text-slate-400 hover:text-brand-600 transition-colors"
                            title="Copy Job ID"
                          >
                            <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>

                      {/* Date */}
                      <td className="px-6 py-4 text-sm text-slate-500 font-medium whitespace-nowrap border-r border-slate-100">
                        {formatDate(task.created_at)}
                      </td>

                      {/* Duration */}
                      <td className="px-6 py-4 border-r border-slate-100 text-center">
                        <span className="text-[12px] font-semibold text-slate-500 font-mono">
                          {formatDuration(task)}
                        </span>
                      </td>

                      {/* Lead count */}
                      <td className="px-6 py-4 border-r border-slate-100 text-center">
                        <span className="text-base font-extrabold text-slate-900 text">
                          {getLeadCount(task).toLocaleString()}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 border-r border-slate-100">
                        <StatusBadge status={task.status} progress={task.progress ?? 0} processedRecords={task.processed_records ?? 0} totalRecords={task.total_records ?? 0} />
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedTask(task)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-[12px] font-semibold transition-colors"
                          >
                            <MagnifyingGlassIcon className="w-3.5 h-3.5" /> Params
                          </button>
                          {task.status === 'completed' && (
                            <button
                              onClick={() => handleDownload(task.id, task.company)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-[12px] font-semibold transition-colors"
                            >
                              <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Export
                            </button>
                          )}
                          <Link
                            to={`/dashboard/search_history/${task.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-50 hover:bg-brand-100 text-slate-800 text-[12px] font-semibold transition-colors"
                          >
                            <EyeIcon className="w-3.5 h-3.5" /> View
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {meta.total > 0 && (
              <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[12px] text-slate-400 font-medium">
                  Showing {start} to {end} of {tasks.length} lists
                </span>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-brand-600 disabled:opacity-30 text-sm transition-colors"
                    >‹</button>
                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`w-8 h-8 flex items-center justify-center rounded-full text-[13px] font-bold transition-colors ${
                          currentPage === i + 1
                            ? 'bg-brand-500 text-slate-900'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >{i + 1}</button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-brand-600 disabled:opacity-30 text-sm transition-colors"
                    >›</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bottom cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="relative bg-slate-900 rounded-2xl p-6 overflow-hidden">
              <div className="relative z-10">
                <p className="text-lg font-extrabold text-slate-900 mb-1">Use our API</p>
                <p className="text-sm text-slate-400 mb-5">Connect your sales tools directly to our API seamlessly.</p>
                <Link
                  to="/dashboard/api-access"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 px-4 py-2 rounded-xl text-sm font-bold hover:bg-slate-100 transition-colors"
                >
                  <CodeBracketSquareIcon className="w-4 h-4" /> Configure API
                </Link>
              </div>
              <div className="absolute right-4 bottom-2 text-[120px] font-black text-slate-900/5 leading-none select-none">⌗</div>
            </div>

            <div className="bg-brand-50 border border-brand-100 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <SparklesIcon className="w-4 h-4 text-brand-500" />
                <span className="text-[10px] font-bold text-brand-500 uppercase tracking-wider">Precision Tip</span>
              </div>
              <p className="text-lg font-extrabold text-slate-900 mb-1">In-depth Analysis</p>
              <p className="text-sm text-slate-500 mb-5">Enable "Deep Search" on your next list to find more decision makers for every company you upload.</p>
              <Link
                to="/dashboard/search"
                className="inline-flex items-center gap-2 bg-brand-500 text-slate-900 px-4 py-2 rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors [box-shadow:0_4px_12px_rgba(0,84,214,0.3)]"
              >
                Try Deep Search
              </Link>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
